import type { TicketRequestCategoryI } from 'declarations/interfaces/ticketRequestCategories';
import type { TicketRequestSubcategoryI } from 'declarations/interfaces/ticketRequestSubcategories';
import type { SelectOptionT } from 'declarations/types/select';

/** Delimiter for combined select values (categoryId + subcategoryId). */
export const COMBINED_CATEGORY_SUBCATEGORY_VALUE_SEPARATOR = '|';

export const formatCombinedCategorySubcategoryLabel = (
  categoryName: string,
  subcategoryName: string
): string => `${categoryName} : ${subcategoryName}`;

export const encodeCombinedCategorySubcategoryValue = (
  categoryId: number,
  subcategoryId: number
): string => `${categoryId}${COMBINED_CATEGORY_SUBCATEGORY_VALUE_SEPARATOR}${subcategoryId}`;

export const parseCombinedCategorySubcategoryValue = (
  value: string | number
): { categoryId: number; subcategoryId: number } | null => {
  const raw = String(value);
  const [categoryPart, subcategoryPart] = raw.split(COMBINED_CATEGORY_SUBCATEGORY_VALUE_SEPARATOR);
  if (!categoryPart || !subcategoryPart) {
    return null;
  }
  const categoryId = Number(categoryPart);
  const subcategoryId = Number(subcategoryPart);
  if (!Number.isFinite(categoryId) || !Number.isFinite(subcategoryId)) {
    return null;
  }
  return { categoryId, subcategoryId };
};

export const buildCombinedCategorySubcategoryOptions = (
  categories: TicketRequestCategoryI[],
  subcategoriesByCategoryId: Record<number, TicketRequestSubcategoryI[]>
): SelectOptionT[] => {
  const options: SelectOptionT[] = [];

  for (const category of categories) {
    const subcategories = subcategoriesByCategoryId[category.id] ?? [];
    for (const subcategory of subcategories) {
      options.push({
        label: formatCombinedCategorySubcategoryLabel(category.categoryName, subcategory.name),
        value: encodeCombinedCategorySubcategoryValue(category.id, subcategory.id),
      });
    }
  }

  return options.sort((a, b) => a.label.localeCompare(b.label));
};
